/**
 * Cohorts list — the operator's global cohort view.
 *
 * GLOBAL per ADR-0052 D1: one table across every client, with the client
 * as a column and a filter, never the navigation entrypoint. The questions
 * this screen exists for are cross-client — "which cohorts start next
 * week?", "which end this month?" — and answering them by drilling into
 * each client in turn is O(clients) page loads.
 *
 * Defaults to soonest-start-first (NULLs last), with server-side sorting
 * by start, end, or creation time. Four inclusive date bounds let operators
 * combine start and end windows.
 *
 * Creating a cohort still happens from a client, because a cohort cannot
 * exist without one. That asymmetry is deliberate: reads global, writes
 * targeted.
 *
 * English-only internal tooling outside lesson-content review.
 */
import { useEffect, useState } from 'react'
import { FilterTh } from '../common/column-filter-indicator'
import { SortableTh, type SortDir } from '../common/sortable-th'
import type { CohortSortField } from './types'
import { Badge, Button, buttonVariants, Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { DateFilterInput } from '../common/date-filter-input'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys'
import {
  ROW_LINK,
  ROW_LINK_ABOVE,
  TABLE,
  TABLE_WRAP,
  TABLE_HEAD_ROW,
  TABLE_ROW,
} from '../common/table'
import { Link, useSearchParams } from 'react-router-dom'
import { ClientPicker } from '../clients/client-picker'
import {
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
} from '../common/list-page'
import { adminCohortClient, messageForCohortError } from './api'
import { todayJstIso } from '../common/access-window'
import { InternalBadge } from './cohort-form-fields'
import { CohortListFunnel } from './cohort-funnel'

type LoadStatus = 'loading' | 'loaded' | 'error'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 250

/** Today + `days`, as an ISO date. For the window quick-filters. */
function isoInDays(days: number): string {
  const d = new Date(`${todayJstIso()}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

const DATE_FILTER_KEYS = [
  'startingBefore',
  'startingAfter',
  'endingBefore',
  'endingAfter',
] as const
type DateFilters = Partial<Record<(typeof DATE_FILTER_KEYS)[number], string>>

const createdAtFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit',
})

export function CohortsListPage(): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  // Filters live in the URL so a filtered view is shareable and survives a
  // refresh — the same reasoning as the students page's clientId.
  const [searchParams, setSearchParams] = useSearchParams()
  const clientId = searchParams.get('clientId') ?? ''
  const endingBefore = searchParams.get('endingBefore') ?? ''
  const startingBefore = searchParams.get('startingBefore') ?? ''
  const endingAfter = searchParams.get('endingAfter') ?? ''
  const startingAfter = searchParams.get('startingAfter') ?? ''
  const trialFilter = searchParams.get('isTrial') ?? ''
  const rawSort = searchParams.get('sortField')
  const sortField: CohortSortField = rawSort === 'accessEndsOn' || rawSort === 'createdAt'
    ? rawSort : 'scheduledStartOn'
  const sortDirection: SortDir = searchParams.get('sortDirection') === 'desc' ? 'desc' : 'asc'
  const [page, setPage] = useState(0)

  function setSort(key: CohortSortField): void {
    const next = new URLSearchParams(searchParams)
    next.set('sortField', key)
    next.set('sortDirection', key === sortField
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : (key === 'createdAt' ? 'desc' : 'asc'))
    setSearchParams(next, { replace: true })
    setPage(0)
  }
  const endingSoonDate = isoInDays(30)
  const startingSoonDate = isoInDays(7)
  const today = todayJstIso()
  const activeTodayFilters = { startingBefore: today, endingAfter: today }
  function matchesDateShortcut(filters: DateFilters): boolean {
    return DATE_FILTER_KEYS.every(
      (key) => (searchParams.get(key) ?? '') === (filters[key] ?? ''),
    )
  }
  const endingSoonActive = matchesDateShortcut({ endingBefore: endingSoonDate })
  const startingSoonActive = matchesDateShortcut({ startingBefore: startingSoonDate })
  const activeToday = matchesDateShortcut(activeTodayFilters)

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  // Paging is server-side here, so `offset` belongs in the key — page 2 is
  // a different cache entry from page 1, and stepping back to page 1 is
  // instant instead of a refetch.
  const params = {
    sortField,
    sortDirection,
    clientId: clientId || undefined,
    search: search || undefined,
    startingBefore: startingBefore || undefined,
    endingBefore: endingBefore || undefined,
    endingAfter: endingAfter || undefined,
    startingAfter: startingAfter || undefined,
    isTrial:
      trialFilter === 'true'
        ? true
        : trialFilter === 'false'
          ? false
          : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const cohortsQuery = useQuery({
    queryKey: queryKeys.cohortList(params),
    queryFn: () => adminCohortClient.listAll(params),
    // Typing in the search mints a new key per keystroke; without this the
    // table unmounts into "Loading…" and FLASHES on every character. The
    // previous rows stay (dimmed below) until the next page arrives.
    placeholderData: keepPreviousData,
  })

  const cohorts = cohortsQuery.data?.cohorts ?? []
  const total = cohortsQuery.data?.total ?? 0
  const startDescription = [
    startingAfter && `On or after ${startingAfter}`,
    startingBefore && `On or before ${startingBefore}`,
  ].filter(Boolean).join('; ')
  const endDescription = [
    endingAfter && `On or after ${endingAfter}`,
    endingBefore && `On or before ${endingBefore}`,
  ].filter(Boolean).join('; ')
  const cohortDescription = [
    search && `Name contains “${search}”`,
    trialFilter === 'true' && 'Trials only',
    trialFilter === 'false' && 'Non-trials only',
  ].filter(Boolean).join('; ')
  const clientDescription = clientId
    ? (cohorts.find((cohort) => cohort.clientId === clientId)?.clientName ?? 'Selected client')
    : ''
  const status: LoadStatus = cohortsQuery.isPending
    ? 'loading'
    : cohortsQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    cohortsQuery.error !== null
      ? messageForCohortError(cohortsQuery.error)
      : null

  function setFilter(key: string, value: string): void {
    if (value) searchParams.set(key, value)
    else searchParams.delete(key)
    setSearchParams(searchParams, { replace: true })
    setPage(0)
  }

  function setDateShortcut(filters: DateFilters): void {
    const active = matchesDateShortcut(filters)
    const next = new URLSearchParams(searchParams)
    for (const key of DATE_FILTER_KEYS) next.delete(key)
    if (!active) {
      for (const [key, value] of Object.entries(filters)) next.set(key, value)
    }
    setSearchParams(next, { replace: true })
    setPage(0)
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasFilters =
    search !== '' ||
    clientId !== '' ||
    startingBefore !== '' ||
    endingBefore !== '' ||
    endingAfter !== '' ||
    startingAfter !== '' ||
    trialFilter !== ''

  // Hoisted so the empty state offers the SAME action as the rail rather than a
  // second implementation of it.
  function clearFilters(): void {
    setQuery('')
    const next = new URLSearchParams(searchParams)
    for (const key of [...DATE_FILTER_KEYS, 'clientId', 'isTrial']) next.delete(key)
    setSearchParams(next, { replace: true })
    setPage(0)
  }

  return (
    <ListPage
      title="Cohorts"
      count={status === 'loaded' ? total : undefined}
      action={
        clientId !== '' ? (
          <Link
            to={`/admin/clients/${encodeURIComponent(clientId)}/cohorts/new`}
            className={
              buttonVariants({ variant: 'primary' }) + ' whitespace-nowrap'
            }
          >
            <Icon name="add" />
            New cohort
          </Link>
        ) : undefined
      }
      search={
        <SearchField
          id="cohort-search"
          label="Search cohorts"
          placeholder="Search cohort name..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setPage(0)
          }}
        />
      }
      // The rail's filters only; the search stays visible when collapsed.
      activeFilterCount={
        [clientId, startingBefore, startingAfter, endingBefore, endingAfter, trialFilter]
          .filter((value) => value !== '').length
      }
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={clientId !== ''} label="Client" htmlFor="cohort-client-filter">
            <ClientPicker
              id="cohort-client-filter"
              label="Client"
              emptyLabel="All clients"
              value={clientId}
              onChange={(next) => setFilter('clientId', next)}
              showDetails={false}
              size="sm"
            />
          </FilterField>
          <FilterField active={startingBefore !== ''} label="Starting on or before" htmlFor="cohort-starting-before">
            <DateFilterInput
              id="cohort-starting-before"
              label="Starting on or before"
              value={startingBefore}
              onValueChange={(value) => setFilter('startingBefore', value)}
              size="sm"
            />
          </FilterField>
          <FilterField active={startingAfter !== ''} label="Starting on or after" htmlFor="cohort-starting-after">
            <DateFilterInput
              id="cohort-starting-after"
              label="Starting on or after"
              value={startingAfter}
              onValueChange={(value) => setFilter('startingAfter', value)}
              size="sm"
            />
          </FilterField>
          <FilterField active={endingBefore !== ''} label="Ending on or before" htmlFor="cohort-ending-before">
            <DateFilterInput
              id="cohort-ending-before"
              label="Ending on or before"
              value={endingBefore}
              onValueChange={(value) => setFilter('endingBefore', value)}
              size="sm"
            />
          </FilterField>
          <FilterField active={endingAfter !== ''} label="Ending on or after" htmlFor="cohort-ending-after">
            <DateFilterInput
              id="cohort-ending-after"
              label="Ending on or after"
              value={endingAfter}
              onValueChange={(value) => setFilter('endingAfter', value)}
              size="sm"
            />
          </FilterField>
          <FilterField active={trialFilter !== ''} label="Trial" htmlFor="cohort-trial-filter">
            <Select
              value={trialFilter || 'all'}
              onValueChange={(next) =>
                setFilter('isTrial', next === 'all' ? '' : next)
              }
            >
              <SelectTrigger id="cohort-trial-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cohorts</SelectItem>
                <SelectItem value="true">Trials only</SelectItem>
                <SelectItem value="false">Non-trials only</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
          <div
            className="space-y-2"
            role="group"
            aria-label="Date shortcuts"
          >
            <Button
              type="button"
              onClick={() => setDateShortcut(activeTodayFilters)}
              aria-pressed={activeToday}
              variant="secondary"
              size="sm"
              className={`w-full justify-between ${
                activeToday ? 'border-accent bg-accent-soft hover:bg-accent/30' : ''
              }`}
            >
              Active today
              <Icon name="selected" className={`shrink-0 text-text-primary ${activeToday ? '' : 'invisible'}`} />
            </Button>
            <Button
              type="button"
              onClick={() =>
                setDateShortcut({ startingBefore: startingSoonDate })
              }
              aria-pressed={startingSoonActive}
              variant="secondary"
              size="sm"
              className={`w-full justify-between ${
                startingSoonActive
                  ? 'border-accent bg-accent-soft hover:bg-accent/30'
                  : ''
              }`}
            >
              Starting within 7 days
              <Icon name="selected" className={`shrink-0 text-text-primary ${startingSoonActive ? '' : 'invisible'}`} />
            </Button>
            <Button
              type="button"
              onClick={() => setDateShortcut({ endingBefore: endingSoonDate })}
              aria-pressed={endingSoonActive}
              variant="secondary"
              size="sm"
              className={`w-full justify-between ${
                endingSoonActive
                  ? 'border-accent bg-accent-soft hover:bg-accent/30'
                  : ''
              }`}
            >
              Ending within 30 days
              <Icon name="selected" className={`shrink-0 text-text-primary ${endingSoonActive ? '' : 'invisible'}`} />
            </Button>
          </div>
        </>
      }
      pagination={
        status === 'loaded' && cohorts.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </>
            }
            page={page}
            pageCount={pageCount}
            previousDisabled={page === 0}
            nextDisabled={page >= pageCount - 1}
            onPrevious={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          />
        ) : undefined
      }
    >
      <>
        {status === 'loading' && (
          <p className="text-text-secondary">Loading cohorts…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load cohorts</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}
        {status === 'loaded' && cohorts.length === 0 && (
          <FilteredEmptyState
            hasFilters={hasFilters}
            noun="cohorts"
            onClear={clearFilters}
            emptyHint="No cohorts yet. Open a client and create one — it carries the dates and defaults its members inherit."
          />
        )}

        {/* Stale rows stay visible while the next result is in flight
            (keepPreviousData) — dimmed, so the refresh is honest without
            the table unmounting into a flash of "Loading…". */}
        {status === 'loaded' && cohorts.length > 0 && (
          <div
            className={
              cohortsQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
          <>
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  <FilterTh label="Cohort" filterDescription={cohortDescription} className="whitespace-nowrap p-3 font-medium" />
                  <FilterTh label="Client" filterDescription={clientDescription} className="whitespace-nowrap p-3 font-medium" />
                  <SortableTh filterDescription={startDescription} label="Starts" columnKey="scheduledStartOn" activeKey={sortField} dir={sortDirection} onSort={setSort} />
                  <SortableTh filterDescription={endDescription} label="Ends" columnKey="accessEndsOn" activeKey={sortField} dir={sortDirection} onSort={setSort} />
                  <th className="whitespace-nowrap p-3 font-medium">HR users</th>
                  <th className="whitespace-nowrap p-3 font-medium">Students</th>
                  <th className="whitespace-nowrap p-3 font-medium">Funnel</th>
                  <th className="whitespace-nowrap p-3 font-medium">Tier</th>
                  <SortableTh label="Created at" columnKey="createdAt" activeKey={sortField} dir={sortDirection} onSort={setSort} title="Creation date and time (JST)" />
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => (
                  <tr
                    key={c.cohortId}
                    className={TABLE_ROW}
                  >
                    <td className="p-3">
                      <Link
                        to={`/admin/clients/${c.clientId}/cohorts/${c.cohortId}`}
                        className={ROW_LINK}
                      >
                        {c.name}
                      </Link>
                      {c.resolvedIsInternal && (
                        <span className="ml-2">
                          <InternalBadge />
                        </span>
                      )}
                      {c.isTrial && (
                        <span className="ml-2">
                          <Badge variant="muted">Trial</Badge>
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-text-secondary">
                      {/* A DIFFERENT destination from the row link, so it
                          must sit above the overlay or the row swallows it. */}
                      <Link
                        to={`/admin/clients/${c.clientId}`}
                        className={`underline ${ROW_LINK_ABOVE}`}
                      >
                        {c.clientName}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap p-3">
                      {c.scheduledStartOn ?? (
                        <span className="text-text-secondary">on activation</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap p-3">{c.accessEndsOn}</td>
                    <td className="p-3 tabular-nums">{c.hrUserCount}</td>
                    <td className="p-3 tabular-nums">{c.memberCount}</td>
                    <td className="p-3"><CohortListFunnel cohort={c} /></td>
                    <td className="p-3 text-text-secondary">
                      {c.defaultTier}
                    </td>
                    <td className="whitespace-nowrap p-3 tabular-nums text-text-secondary">
                      <time dateTime={c.createdAt}>
                        {createdAtFormatter.format(new Date(c.createdAt))}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

          </>
          </div>
        )}
      </>
    </ListPage>
  )
}
